import {
  AppstoreOutlined,
  BarChartOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  MobileOutlined,
  PlusOutlined,
  SettingOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Layout, Menu, Skeleton, theme } from "antd";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { PermissionKey, PERMISSIONS } from "../../constants/permissions";
import { RootState } from "../../store";

const { Sider } = Layout;

interface DashboardSidebarProps {
  collapsed: boolean;
  onItemClick?: () => void;
}

type MenuItem = {
  key: string;
  permissionKey: PermissionKey;
  icon?: React.ReactNode;
  label: string;
  path?: string;
  children?: MenuItem[];
};

type AntdMenuItem = Required<MenuProps>["items"][number];

const menuConfig: MenuItem[] = [
  {
    key: "dashboard",
    permissionKey: PERMISSIONS.VIEW_DASHBOARD.name,
    icon: <DashboardOutlined />,
    label: "Dashboard",
    path: PERMISSIONS.VIEW_DASHBOARD.route,
  },
  {
    key: "sellers",
    permissionKey: PERMISSIONS.LIST_SELLERS.name,
    icon: <UserSwitchOutlined />,
    label: "Sellers",
    children: [
      {
        key: "seller-add",
        permissionKey: PERMISSIONS.ADD_SELLER.name,
        icon: <PlusOutlined />,
        label: "Register",
        path: PERMISSIONS.ADD_SELLER.route,
      },
      {
        key: "seller-list",
        permissionKey: PERMISSIONS.LIST_SELLERS.name,
        icon: <UnorderedListOutlined />,
        label: "List",
        path: PERMISSIONS.LIST_SELLERS.route,
      },
      {
        key: "seller-manage-reference",
        permissionKey: PERMISSIONS.MANAGE_REFERENCE.name,
        icon: <ShareAltOutlined />,
        label: "Manage References",
        path: PERMISSIONS.MANAGE_REFERENCE.route,
      },
    ],
  },
  {
    key: "tokens",
    permissionKey: PERMISSIONS.VIEW_SALES_REPORT.name,
    icon: <ShoppingCartOutlined />,
    label: "Tokens",
    children: [
      {
        key: "sales-sell-token",
        permissionKey: PERMISSIONS.SELL_TOKEN.name,
        icon: <ShoppingCartOutlined />,
        label: "Sell Token",
        path: PERMISSIONS.SELL_TOKEN.route,
      },
      {
        key: "sales-list",
        permissionKey: PERMISSIONS.VIEW_SALES_LIST.name,
        icon: <UnorderedListOutlined />,
        label: "Sales List",
        path: PERMISSIONS.VIEW_SALES_LIST.route,
      },
      {
        key: "sales-report",
        permissionKey: PERMISSIONS.VIEW_SALES_REPORT.name,
        icon: <BarChartOutlined />,
        label: "Sales Report",
        path: PERMISSIONS.VIEW_SALES_REPORT.route,
      },
      {
        key: "tokens-distribute-token",
        icon: <ShareAltOutlined />,
        label: "Share Token",
        path: PERMISSIONS.DISTRIBUTE_TOKEN.route,
        permissionKey: PERMISSIONS.DISTRIBUTE_TOKEN.name,
      },
      {
        key: "tokens-transaction-history",
        icon: <ShareAltOutlined />,
        label: "Transaction History",
        path: PERMISSIONS.TRANSACTION_HISTORY.route,
        permissionKey: PERMISSIONS.TRANSACTION_HISTORY.name,
      },
    ],
  },
  // {
  //   key: "permissions",
  //   permissionKey: PERMISSIONS.VIEW_PERMISSIONS.name,
  //   icon: <KeyOutlined />,
  //   label: "Permissions",
  //   path: PERMISSIONS.VIEW_PERMISSIONS.route,
  // },
  {
    key: "device",
    permissionKey: PERMISSIONS.REGISTER_DEVICE.name,
    icon: <MobileOutlined />,
    label: "Device",
    children: [
      {
        key: "device-register",
        permissionKey: PERMISSIONS.REGISTER_DEVICE.name,
        icon: <PlusOutlined />,
        label: "Register",
        path: PERMISSIONS.REGISTER_DEVICE.route,
      },
      {
        key: "device-list",
        permissionKey: PERMISSIONS.LIST_DEVICES.name,
        icon: <UnorderedListOutlined />,
        label: "List",
        path: PERMISSIONS.LIST_DEVICES.route,
      },
    ],
  },
  {
    key: "app-info",
    permissionKey: PERMISSIONS.ADD_APP_INFO.name,
    icon: <AppstoreOutlined />,
    label: "App Info",
    children: [
      {
        key: "app-info-add",
        permissionKey: PERMISSIONS.ADD_APP_INFO.name,
        icon: <PlusOutlined />,
        label: "Add",
        path: PERMISSIONS.ADD_APP_INFO.route,
      },
      {
        key: "app-info-list",
        permissionKey: PERMISSIONS.LIST_APP_INFO.name,
        icon: <UnorderedListOutlined />,
        label: "List",
        path: PERMISSIONS.LIST_APP_INFO.route,
      },
      {
        key: "app-info-bulk-edit",
        permissionKey: PERMISSIONS.BULK_EDIT_APP_INFO.name,
        icon: <EditOutlined />,
        label: "Bulk Edit",
        path: PERMISSIONS.BULK_EDIT_APP_INFO.route,
      },
      {
        key: "app-upload",
        permissionKey: PERMISSIONS.UPLOAD_APPS.name,
        icon: <UploadOutlined />,
        label: "Upload APP",
        path: PERMISSIONS.UPLOAD_APPS.route,
      },
    ],
  },
  {
    key: "admin",
    permissionKey: PERMISSIONS.MANAGE_CACHE.name,
    icon: <SettingOutlined />,
    label: "Administration",
    children: [
      {
        key: "cache-management",
        permissionKey: PERMISSIONS.MANAGE_CACHE.name,
        icon: <DatabaseOutlined />,
        label: "Cache",
        path: PERMISSIONS.MANAGE_CACHE.route,
      },
      {
        key: "blocked-ips",
        permissionKey: PERMISSIONS.MANAGE_BLOCKED_IPS.name,
        icon: <LockOutlined />,
        label: "Blocked IPs",
        path: PERMISSIONS.MANAGE_BLOCKED_IPS.route,
      },
    ],
  },
];

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ collapsed, onItemClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const { permissions, isLoading } = useSelector(
    (state: RootState) => state.auth
  );

  const permissionKeys = new Set(permissions?.map((p: any) => p.name) || []);

  const hasPermission = (item: MenuItem): boolean => {
    // if (isLoading) return true;
    if (permissionKeys.has(item.permissionKey)) return true;
    if (item.children) {
      return item.children.some((child) =>
        permissionKeys.has(child.permissionKey)
      );
    }
    return false;
  };

  const convertToAntdMenuItem = (item: MenuItem): AntdMenuItem => {
    if (!hasPermission(item)) return null;

    const menuItem = {
      key: item.path || item.key,
      icon: item.icon,
      label: item.label,
      onClick: item.path ? () => {
        navigate(item.path!);
        // Collapse sidebar on link click for both mobile and desktop
        if (!collapsed) {
          onItemClick?.();
        }
      } : undefined,
    };

    if (item.children) {
      const children = item.children.map(convertToAntdMenuItem).filter(Boolean);

      if (children.length === 0) return null;

      return {
        ...menuItem,
        children,
      };
    }

    return menuItem;
  };

  const items = menuConfig.map(convertToAntdMenuItem).filter(Boolean);

  const getDefaultOpenKeys = () => {
    const currentPath = location.pathname;
    return menuConfig
      .filter((item) =>
        item.children?.some((child) => child.path === currentPath)
      )
      .map((item) => item.key);
  };

  const renderSidebarContent = () => {
    if (isLoading) {
      return (
        <div
          style={{
            padding: "24px 16px",
            animation: "fadeIn 0.3s ease-in-out",
          }}
        >
          {Array(8)
            .fill(null)
            .map((_, index) => (
              <Skeleton.Button
                key={index}
                active
                size="large"
                block
                style={{
                  marginBottom: "16px",
                  animation: `shimmer 1.4s ease infinite`,
                  animationDelay: `${index * 0.1}s`,
                  background:
                    "linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 37%, #f2f2f2 63%)",
                  backgroundSize: "400% 100%",
                }}
              />
            ))}
        </div>
      );
    }

    return (
      <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
        <Menu
          mode="inline"
          defaultOpenKeys={getDefaultOpenKeys()}
          selectedKeys={[location.pathname]}
          style={{ 
            borderRight: 0,
            transition: "all 0.2s"
          }}
          inlineCollapsed={collapsed}
          items={items}
        />
      </div>
    );
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={0}
      width={256}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
        transition: "all 0.2s ease",
        boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
      }}
      theme="light"
      className="dashboard-sider"
    >
      <div
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          transition: "all 0.3s ease",
          overflow: "hidden",
        }}
      >
        <img
          src="https://emi.shilpon.com/logo192.png"
          alt=""
          style={{ width: "24px", height: "24px", objectFit: "contain" }}
        />
        <span
          style={{
            marginLeft: 8,
            fontSize: "18px",
            fontWeight: "bold",
            transition: "all 0.3s ease",
            opacity: collapsed ? 0 : 1,
            transform: collapsed ? "translateX(-20px)" : "translateX(0)",
            whiteSpace: "nowrap",
          }}
        >
          EMI Control
        </span>
      </div>
      {renderSidebarContent()}
      <style>
        {`
          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateX(-20px);
            }
            to { 
              opacity: 1; 
              transform: translateX(0);
            }
          }
          
          @keyframes shimmer {
            0% { background-position: -400% 0; }
            100% { background-position: 400% 0; }
          }
          
          .ant-menu-item, .ant-menu-submenu-title {
            transition: all 0.3s ease !important;
          }
          
          .ant-menu-item:hover, .ant-menu-submenu-title:hover {
            transform: translateX(4px);
          }
          
          .ant-menu-item-selected {
            position: relative;
            overflow: hidden;
          }
          
          .ant-menu-item-selected::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 4px;
            height: 100%;
            background: ${token.colorPrimary};
            animation: slideIn 0.3s ease;
          }
          
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}
      </style>
    </Sider>
  );
};

export default DashboardSidebar;
